import { useMutation } from "@apollo/client";
import type React from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { gql, Participant } from "@medbillai/generated-graphql-types";
import { retry } from "@medbillai/utils";
import { type DocumentScanEntryPoint, deriveBatchScanbotEntryPoint } from "@medbillai/utils/documentScan";
import { getBundledUploadSelectionError, isArchiveFile, UNSUPPORTED_FILE_TYPE_MESSAGE } from "@medbillai/utils/files";
import { analyticsService } from "../../lib/analytics";
import type { UploadErrorType, UploadFailureStage } from "../../lib/analytics/types";
import { sumDefinedSizes, type UploadFunnelAttempt } from "../../lib/analytics/uploadFunnel";
import { clearInFlightUpload, trackInFlightUpload, updateInFlightUploadStage } from "../../lib/analytics/uploadLifecycle";
import { errorHandling } from "../../lib/apollo/utils";
import { singularService } from "../../lib/singular/singular";
import { SingularEvents } from "../../lib/singular/types";
const createMultiFileDocumentMutation = gql(/* GraphQL */`
  mutation CreateMultiFileDocument($input: CreateMultiFileDocumentInput!) {
    createMultiFileDocument(input: $input) {
      document {
        id
      }
      files {
        s3Key
        url
        name
      }
    }
  }
`);
const finalizeDocumentMutation = gql(/* GraphQL */`
  mutation FinalizeDocument($input: FinalizeDocumentInput!) {
    finalizeDocument(input: $input) {
      document {
        id
      }
    }
  }
`);
export type Upload = {
  id: string;
  documentId?: string;
  fileCount: number;
  contentType?: string;
};
export type UploadFile = {
  name: string;
  sizeBytes?: number;
  contentType: string;
  blob: Blob;
  // Surface the Scanbot scan was launched from. Absent for picked files —
  // the server scopes the scan-quality experiment's downstream guardrail
  // metrics on it, and anything it doesn't recognise is excluded.
  scanbotEntryPoint?: DocumentScanEntryPoint;
};
export type UploadInput = {
  key: string;
  uploadFiles: UploadFile[];
  issueId?: string;
  // Optional document type to skip ingestion classification.
  // Valid values defined in ts/apps/api/src/vision/types.ts (DocumentType).
  dataType?: string;
  // When set to false, the uploaded document won't appear in the chat timeline.
  userVisible?: boolean;
  // When set to false, the document is ingested without engaging the case
  // agent (no triage, no analysis chat replies). For uploads that are form
  // inputs rather than case activity — the FA wizard's document fields.
  startAnalysis?: boolean;
  // The frozen funnel attempt that picked these files, minted by the upload
  // sheet and threaded here explicitly rather than read from shared state, so
  // a sheet reopened mid-batch can't re-attribute an in-flight upload's events
  // or persisted provenance. Absent for uploads that bypassed the sheet
  // (drag-drop, programmatic) — those emit no funnel events.
  funnelSession?: UploadFunnelAttempt;
  onUploadComplete: (documentId: string) => Promise<unknown>;
  onUploadStarted: () => void;
  onUploadError: (error: UploadError) => void;
};
export interface UploadDocumentContextInterface {
  uploads: Record<string, Upload[]>;
  upload: (uploadInput: UploadInput) => Promise<void>;
}
export const initialContext: UploadDocumentContextInterface = {
  uploads: {},
  upload: () => {
    throw new Error("Context not initialized");
  }
};
export const UploadDocumentContext = createContext<UploadDocumentContextInterface>(initialContext);
export interface UploadDocumentProviderProps {
  children?: React.ReactNode;
}
export type UploadError = {
  uploadId?: string;
  // User-friendly message to display to the user.
  message?: string;
  // The actual error that occurred.
  error?: Error;
};
class S3UploadError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = "S3UploadError";
  }
}
const isTransientS3Error = (error: unknown): boolean => error instanceof S3UploadError && (error.status === 503 || error.status === 500 || error.status === 429);

/**
 * `uploadFileToS3WithRetry` only retries transient statuses, so an error that
 * escapes the S3 leg after a transient classification means the retries ran
 * out — a capacity signal, worth separating from a hard rejection.
 */
const uploadErrorTypeFor = (stage: UploadFailureStage, error: unknown): UploadErrorType => {
  if (stage === "CREATE") {
    return "CREATE_FAILED";
  }
  if (stage === "FINALIZE") {
    return "FINALIZE_FAILED";
  }
  return isTransientS3Error(error) ? "S3_TRANSIENT_EXHAUSTED" : "S3_FAILED";
};
const uploadFileToS3WithRetry = async (url: string, blob: Blob, contentType: string, fileName: string): Promise<void> => {
  await retry(async () => {
    const response = await fetch(url, {
      method: "PUT",
      body: blob,
      headers: {
        "Content-Type": contentType
      }
    });
    if (!response.ok) {
      throw new S3UploadError(`S3 upload failed for ${fileName}: ${response.status}`, response.status);
    }
  }, {
    times: 3,
    delay: 1000,
    backoff: 2,
    shouldRetry: isTransientS3Error
  });
};

/*
 * A high level provider for document uploading
 * Its purpose is to:
 * - Keep track of the upload status of documents.
 * - Enable parallel uploads of multiple documents
 *   and ensure a process in the background even if
 *   the user navigates away from the screen where the upload
 *   is initiated.
 */
export const UploadDocumentProvider = ({
  children
}: UploadDocumentProviderProps) => {
  const [uploads, setUploads] = useState<Record<string, Upload[]>>({});
  const [createMultiFileDocument] = useMutation(createMultiFileDocumentMutation, {
    context: errorHandling("no-log"),
    ignoreResults: true
  });
  const [finalizeDocument] = useMutation(finalizeDocumentMutation, {
    context: errorHandling("no-log"),
    ignoreResults: true
  });

  // Careful here to only pass updater methods to the state `set`.
  // Each uploads calling this method closes over the current state
  // and we don't want to reset the state to a stale value.
  const addUpload = useCallback((newUpload: Upload, key: string) => {
    setUploads(curr => ({
      ...curr,
      [key]: [...(curr[key] ?? []), newUpload]
    }));
  }, []);
  const removeUpload = useCallback((uploadId: string, key: string) => {
    setUploads(curr => {
      const remainingUploadsForKey = (curr[key] ?? []).filter(upload => upload.id !== uploadId);
      if (remainingUploadsForKey.length === 0) {
        // Remove the key property from the object since there are no uploads left for this key.
        const rest = Object.fromEntries(Object.entries(curr).filter(([k]) => k !== key));
        return rest;
      }
      return {
        ...curr,
        [key]: remainingUploadsForKey
      };
    });
  }, []);
  const updateUpload = useCallback((uploadId: string, updates: Partial<Upload>, key: string) => {
    setUploads(curr => curr[key] ? {
      ...curr,
      [key]: curr[key].map(upload => upload.id === uploadId ? {
        ...upload,
        ...updates
      } : upload)
    } : curr);
  }, []);
  const upload = useCallback(async ({
    key,
    uploadFiles,
    issueId,
    dataType,
    userVisible,
    startAnalysis,
    funnelSession,
    onUploadStarted,
    onUploadComplete,
    onUploadError
  }: UploadInput) => {
    if (uploadFiles.length === 0) {
      throw new Error("No files to upload");
    }

    // Outcome events belong here, not at each call site: three of the five
    // upload surfaces had no failure telemetry at all under that convention.
    // The envelope comes from the sheet that opened this attempt; per-surface
    // events (the FA wizard's) still fire at their own call sites. Set up
    // above the archive guard so a rejected batch still gets a terminal.
    const totalSizeBytes = sumDefinedSizes(uploadFiles.map(file => file.sizeBytes));
    const funnelBase = funnelSession ? {
      ...funnelSession,
      fileCount: uploadFiles.length,
      ...(totalSizeBytes !== undefined ? {
        totalSizeBytes
      } : {})
    } : undefined;
    const startedAtMs = Date.now();
    let stage: UploadFailureStage = "CREATE";
    // The upload is settled at finalize; the consumer's `onUploadComplete`
    // cache work runs after and must not turn a succeeded upload into a
    // second, contradictory terminal event.
    let funnelSettled = false;
    const trackUploadFailed = (failure: {
      stage?: UploadFailureStage;
      errorType: UploadErrorType;
    }) => {
      if (!funnelBase) {
        return;
      }
      analyticsService.trackEvent("UploadFailed", {
        ...funnelBase,
        ...failure,
        durationMs: Date.now() - startedAtMs
      });
    };

    // Mirrors the server-side rejection in createMultiFileDocument, but
    // with `message` set so every consumer shows the reason instead of
    // its generic "try again" copy.
    if (uploadFiles.some(isArchiveFile)) {
      trackUploadFailed({
        errorType: "ARCHIVE_REJECTED"
      });
      onUploadError({
        message: UNSUPPORTED_FILE_TYPE_MESSAGE
      });
      return;
    }

    // One document may be many images or a single PDF — not mixed / stacked
    // PDFs (viewer + agent only surface the first PDF). FA uploads one file
    // per document, so it never trips this. Filename covers empty/octet-stream
    // MIME the same way the archive guard does.
    const selectionError = getBundledUploadSelectionError(uploadFiles);
    if (selectionError) {
      trackUploadFailed({
        errorType: "BUNDLED_SELECTION_REJECTED"
      });
      onUploadError({
        message: selectionError
      });
      return;
    }
    onUploadStarted();
    const uploadId = uuid().toString();
    if (funnelSession && funnelBase) {
      analyticsService.trackEvent("UploadSubmitted", funnelBase);
      // Durable from here to the terminal in `finally`: an upload the OS
      // kills mid-flight emits nothing in-process, so its `UploadAbandoned`
      // is recovered from this record at the next launch (native only).
      trackInFlightUpload(uploadId, {
        attempt: funnelSession,
        fileCount: uploadFiles.length,
        ...(totalSizeBytes !== undefined ? {
          totalSizeBytes
        } : {}),
        startedAtMs,
        stage
      });
    }
    addUpload({
      id: uploadId,
      fileCount: uploadFiles.length,
      contentType: uploadFiles[0]?.contentType
    }, key);
    try {
      const {
        data
      } = await createMultiFileDocument({
        variables: {
          input: {
            // Guaranteed with the above check; default to first file name.
            name: uploadFiles[0]!.name,
            files: uploadFiles.map(file => ({
              name: file.name,
              sizeBytes: file.sizeBytes,
              contentType: file.contentType
            })),
            participant: Participant.USER,
            ...(dataType ? {
              dataType
            } : {}),
            ...(funnelSession ? {
              uploadMethod: funnelSession.method,
              uploadEntryPoint: funnelSession.entryPoint,
              uploadPlatform: funnelSession.platform,
              uploadSessionId: funnelSession.uploadSessionId
            } : {})
          }
        }
      });
      if (!data?.createMultiFileDocument) {
        funnelSettled = true;
        trackUploadFailed({
          stage,
          errorType: "CREATE_FAILED"
        });
        onUploadError({
          error: new Error("Unexpected empty document from server")
        });
        return;
      }
      const {
        document,
        files
      } = data.createMultiFileDocument;

      // Store the server-generated document ID in the upload record
      updateUpload(uploadId, {
        documentId: document.id
      }, key);

      // Pair by request order, not basename — multi-select can yield two
      // `scan.png`s from different folders, and find-by-name would upload
      // the first blob twice.
      if (files.length !== uploadFiles.length) {
        funnelSettled = true;
        trackUploadFailed({
          stage,
          errorType: "CREATE_FAILED"
        });
        onUploadError({
          error: new Error(`Signed file count (${files.length}) does not match selection (${uploadFiles.length})`)
        });
        return;
      }
      stage = "S3_PUT";
      updateInFlightUploadStage(uploadId, stage);
      for (const [index, file] of files.entries()) {
        const uploadFile = uploadFiles[index]!;
        await uploadFileToS3WithRetry(file.url, uploadFile.blob, uploadFile.contentType, file.name);
      }

      // Provenance rides on the files rather than the call site: a batch is
      // either all scanned or all picked, and every picker funnels through
      // here. Require unanimity so a future mixed batch degrades to excluded
      // rather than inheriting file[0]'s provenance.
      const scanbotEntryPoint = deriveBatchScanbotEntryPoint(uploadFiles.map(file => file.scanbotEntryPoint));
      stage = "FINALIZE";
      updateInFlightUploadStage(uploadId, stage);
      await finalizeDocument({
        variables: {
          input: {
            documentId: document.id,
            issueId,
            ...(userVisible !== undefined ? {
              userVisible
            } : {}),
            ...(startAnalysis !== undefined ? {
              startAnalysis
            } : {}),
            ...(scanbotEntryPoint ? {
              scanbotEntryPoint
            } : {})
          }
        }
      });
      singularService.event(SingularEvents.documentUploaded);
      if (funnelBase) {
        funnelSettled = true;
        analyticsService.trackEvent("UploadSucceeded", {
          ...funnelBase,
          durationMs: Date.now() - startedAtMs
        });
      }

      // Await to prevent finally from
      // removing upload before its returned from graphql
      // to improve loading of the new entry when changing from
      // upload -> event.
      await onUploadComplete(document.id);
    } catch (error) {
      if (!funnelSettled) {
        trackUploadFailed({
          stage,
          errorType: uploadErrorTypeFor(stage, error)
        });
      }
      if (error instanceof Error) {
        onUploadError({
          uploadId,
          error
        });
      } else {
        onUploadError({
          uploadId,
          error: new Error("Unknown error")
        });
      }
    } finally {
      // Every path out of the try has reported a terminal, so the record has
      // done its job; leaving it would abandon an upload that finished.
      clearInFlightUpload(uploadId);
      removeUpload(uploadId, key);
    }
  }, [addUpload, createMultiFileDocument, finalizeDocument, removeUpload, updateUpload]);
  const contextValue = useMemo<UploadDocumentContextInterface>(() => ({
    uploads,
    upload
  }), [uploads, upload]);
  return <UploadDocumentContext.Provider value={contextValue} data-sentry-element="UploadDocumentContext.Provider" data-sentry-component="UploadDocumentProvider" data-sentry-source-file="UploadDocumentProvider.tsx">
      {children}
    </UploadDocumentContext.Provider>;
};
export const useDocumentUpload = (context = UploadDocumentContext): UploadDocumentContextInterface => useContext(context);